import _ from "lodash";
import React, { useRef, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { useMedia } from "react-media";
import { useParams, Link, useHistory } from "react-router-dom";
import { Segment, Grid, Header, Button, Breadcrumb } from "semantic-ui-react";
import { PublicWrapper } from "../../../../components/PublicWrapper";
import { StyledReactMarkdown } from "../../../../components/StyledReactMarkdown";
import networkSelectors from "../../../../selectors/network";
import { getCitizenPermitsType } from "../../../../queries/citizen";
import queryString from "query-string";
import { trackCitizenApply } from "../../../../mixpanel/citizen_apply";
import { RenewalPromptModal } from "../../../../features/permits/components/renewals/RenewalPromptModal";
import StaffOnlyLabel from "../../components/StaffOnlyLabel";
import PlaceholderUntilLoaded from "../../../../components/global_components/PlaceholderUntilLoaded";

export const PermitsFirstPage = ({ location }: any) => {
  const history = useHistory();
  const contentRef = useRef(null);
  const { site, id } = useParams<{ site: string; id: string }>();
  const { userId } = queryString.parse(location.search) as { userId: string };
  const [open, setOpen] = useState(false);
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const permitsTypeQuery = useQuery(["permitsTypes", id], getCitizenPermitsType(networkHeaders, userId), {
    enabled: id,
  });
  const isLoading = permitsTypeQuery.isLoading;
  const renewalPermits = useMemo(
    () => _.get(permitsTypeQuery.data, "data.attributes.renewal_permits", ""),
    [permitsTypeQuery.data],
  );
  const name = useMemo(() => _.get(permitsTypeQuery.data, "data.attributes.name", ""), [permitsTypeQuery.data]);
  const description = useMemo(
    () => _.get(permitsTypeQuery.data, "data.attributes.description", ""),
    [permitsTypeQuery.data],
  );
  const staffOnly = useMemo(
    () => _.get(permitsTypeQuery.data, "data.attributes.staff-only", ""),
    [permitsTypeQuery.data],
  );
  const isSmallScreen = useMedia({ query: "(max-width: 991px)" });
  const hasOpenRenewalPermits = renewalPermits.length > 0;

  const apply = () => {
    if (!hasOpenRenewalPermits) {
      trackCitizenApply(site, name);
      history.push(`/${site}/public/permits/apply?permitsTypeId=${id}`);
    } else {
      setOpen(true);
    }
  };

  useEffect(() => {
    document.title = `${name}`;
  }, [name]);

  return (
    <PublicWrapper homeUrl={`/${site}/public/permits`} contentRef={contentRef}>
      <Grid centered style={{ margin: isSmallScreen ? 8 : 16 }}>
        <RenewalPromptModal
          id={id}
          site={site}
          open={open}
          name={name}
          renewalPermits={renewalPermits}
          onClose={() => setOpen(false)}
        />
        <Grid.Column computer={10} tablet={16} mobile={16}>
          <div>
            <PlaceholderUntilLoaded isLoading={isLoading}>
              <Breadcrumb size={"large"} style={{ textAlign: "center", marginBottom: isSmallScreen ? 0 : 24 }}>
                <Breadcrumb.Section
                  link
                  as={Link}
                  to={{
                    pathname: `/${site}/public/permits`,
                  }}
                >
                  All Permits
                </Breadcrumb.Section>
                <Breadcrumb.Divider icon='right angle' />
                <Breadcrumb.Section>{name}</Breadcrumb.Section>
              </Breadcrumb>
            </PlaceholderUntilLoaded>
            {staffOnly ? <StaffOnlyLabel></StaffOnlyLabel> : <div></div>}
          </div>

          {isSmallScreen && (
            <Segment basic className='tw-mb-6 tw-mt-6 tw-p-0'>
              <Button primary size='huge' onClick={() => apply()}>
                Apply
              </Button>
            </Segment>
          )}
          <Segment style={{ padding: 24 }} className={"tw-mt-0"}>
            <PlaceholderUntilLoaded isLoading={isLoading} lineCount={7}>
              <Header as='h1'> {name} </Header>
              <StyledReactMarkdown>{description}</StyledReactMarkdown>
            </PlaceholderUntilLoaded>
          </Segment>
        </Grid.Column>
        {!isSmallScreen && (
          <Grid.Column width={2} className='tw-mt-12'>
            <Button primary size='huge' onClick={() => apply()} aria-haspopup={hasOpenRenewalPermits ? "dialog" : ""}>
              Apply
            </Button>
          </Grid.Column>
        )}
      </Grid>
    </PublicWrapper>
  );
};

export default PermitsFirstPage;
